import React, { useState, useEffect, useRef } from 'react';
import {
  IconChartArrowsVertical, IconHeadset, IconSpeakerphone,
  IconCalendar, IconAlertTriangle, IconSend, IconSearch,
  IconBrandWhatsapp, IconMail, IconMessage, IconWorld,
  IconInbox, IconUsers, IconMailbox, IconAlertCircle,
  IconTag, IconEye, IconPlus, IconChevronRight, IconChevronLeft,
  IconBold, IconItalic, IconMoodSmile, IconPaperclip, IconSlash,
  IconRobot, IconTicket, IconX, IconCheck, IconChevronDown
} from '@tabler/icons-react';
import { AGENTS } from '../data/industries';

const AGENT_ICONS = {
  abdullah: IconChartArrowsVertical,
  latifa: IconSpeakerphone,
  khalid: IconHeadset,
};

const CHANNEL_ICONS = {
  whatsapp: IconBrandWhatsapp,
  email: IconMail,
  sms: IconMessage,
  web: IconWorld,
};

const CHANNEL_COLORS = {
  whatsapp: '#25D366',
  email: '#3B82F6',
  sms: '#8B5CF6',
  web: '#6B7280',
};

const STATUS_COLORS = {
  active: '#10B981',
  resolved: '#6B7280',
  escalated: '#F59E0B',
};

// ─── Helper: avatar color from id ───
function avatarColor(id) {
  const colors = ['#1B6B93', '#7B2D5F', '#B5651D', '#5B2D8E', '#0D9488', '#DC2626', '#2563EB', '#7C3AED'];
  let hash = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(en, ar, lang) {
  const name = lang === 'ar' ? (ar || en) : (en || ar);
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

function relativeTime(timestamp, lang) {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return lang === 'ar' ? 'الآن' : 'now';
  if (diffMin < 60) return lang === 'ar' ? `${diffMin} د` : `${diffMin}m`;
  if (diffHr < 24) return lang === 'ar' ? `${diffHr} س` : `${diffHr}h`;
  if (diffDay === 1) return lang === 'ar' ? 'أمس' : 'Yesterday';
  if (diffDay < 7) return lang === 'ar' ? `${diffDay} أيام` : `${diffDay}d`;
  return then.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric' });
}

function dateLabel(timestamp, lang) {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diffDay = Math.floor((now - then) / 86400000);
  if (diffDay === 0) return lang === 'ar' ? 'اليوم' : 'Today';
  if (diffDay === 1) return lang === 'ar' ? 'أمس' : 'Yesterday';
  return then.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// ════════════════════════════════════════════════════════════════════
// DEMO MODE — original animated conversation viewer
// ════════════════════════════════════════════════════════════════════
function DemoMode({ lang, t, industry, demoPhase, setDemoPhase }) {
  const convos = industry.conversations || [];
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const chatEndRef = useRef(null);
  const timeoutsRef = useRef([]);
  const isRtl = lang === 'ar';

  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setVisibleMessages([]);
    setIsPlaying(false);

    if (demoPhase >= 1 && demoPhase <= 3) {
      const targetConvo = convos[demoPhase - 1];
      if (!targetConvo) {
        const advTid = setTimeout(() => { setDemoPhase(prev => prev < 4 ? prev + 1 : prev); }, 1000);
        timeoutsRef.current.push(advTid);
        return;
      }
      setSelectedIdx(demoPhase - 1);
      setIsPlaying(true);
      const lastDelay = targetConvo.messages[targetConvo.messages.length - 1]?.delay || 0;
      targetConvo.messages.forEach((msg, i) => {
        const tid = setTimeout(() => {
          setVisibleMessages(prev => [...prev, msg]);
          if (i === targetConvo.messages.length - 1) setIsPlaying(false);
        }, msg.delay);
        timeoutsRef.current.push(tid);
      });
      const advTid = setTimeout(() => { setDemoPhase(prev => prev < 4 ? prev + 1 : prev); }, lastDelay + 2500);
      timeoutsRef.current.push(advTid);
    } else {
      if (convos[selectedIdx]) setVisibleMessages(convos[selectedIdx].messages);
    }
    return () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };
    // eslint-disable-next-line
  }, [demoPhase]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [visibleMessages]);

  const activeConvoIdx = demoPhase >= 1 && demoPhase <= 3 ? demoPhase - 1 : selectedIdx;
  const convo = convos[activeConvoIdx] || convos[0];
  const agent = AGENTS[convo?.agent];
  const AgentIcon = AGENT_ICONS[convo?.agent] || IconChartArrowsVertical;

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 56px)', animation: 'fadeIn 0.3s ease', margin: '-24px' }}>
      <div style={{
        width: 300, borderRight: isRtl ? 'none' : '1px solid #E5E7EB',
        borderLeft: isRtl ? '1px solid #E5E7EB' : 'none',
        background: '#fff', overflow: 'auto', flexShrink: 0, borderRadius: '12px 0 0 12px'
      }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #E5E7EB' }}>
          <input placeholder={t.search} style={{
            width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
            fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#F3F4F6', color: '#1A1A2E'
          }} />
        </div>
        {convos.map((c, i) => {
          const ag = AGENTS[c.agent];
          const AIcon = AGENT_ICONS[c.agent];
          const active = i === activeConvoIdx;
          const lastMsg = c.messages.filter(m => m.from !== 'system').slice(-1)[0];
          return (
            <div key={c.id} onClick={() => { if (demoPhase === 0 || demoPhase === 4) setSelectedIdx(i); }}
              style={{
                padding: '12px 16px', borderBottom: '1px solid #E5E7EB', cursor: 'pointer',
                background: active ? '#F3F0FF' : '#fff', transition: 'background 0.2s',
                borderLeft: !isRtl && active ? '3px solid #5B2D8E' : 'none',
                borderRight: isRtl && active ? '3px solid #5B2D8E' : 'none',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>
                  {lang === 'ar' ? c.customer : c.customerEn}
                </span>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 4,
                  background: ag.bg + '22', fontSize: 11, color: ag.color, fontWeight: 600
                }}>
                  <AIcon size={12} stroke={2} />
                  {lang === 'ar' ? ag.nameAr : ag.name}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lastMsg ? (lang === 'ar' ? lastMsg.ar : lastMsg.en) : ''}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                {(lang === 'ar' ? c.tagsAr : c.tags).map((tag, ti) => (
                  <span key={ti} style={{
                    padding: '2px 6px', borderRadius: 4, background: '#E5E7EB',
                    fontSize: 10, color: '#9CA3AF', fontWeight: 500
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F3F4F6', borderRadius: '0 12px 12px 0' }}>
        <div style={{
          padding: '12px 20px', background: '#fff', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: agent?.bg || '#EBF5FA',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <AgentIcon size={18} color={agent?.color} stroke={1.8} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>{lang === 'ar' ? convo?.customer : convo?.customerEn}</div>
              <div style={{ fontSize: 12, color: agent?.color, fontWeight: 500 }}>
                {lang === 'ar' ? agent?.nameAr : agent?.name} — {lang === 'ar' ? agent?.roleAr : agent?.role}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {convo?.agent === 'abdullah' && (
              <button style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#1B6B93', fontWeight: 500, fontFamily: 'inherit' }}>
                <IconCalendar size={14} /> {t.viewCalendar}
              </button>
            )}
            {convo?.agent === 'khalid' && (
              <button style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#EF4444', fontWeight: 500, fontFamily: 'inherit' }}>
                <IconAlertTriangle size={14} /> {t.escalate}
              </button>
            )}
            {convo?.agent === 'latifa' && (
              <button style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#B5651D', fontWeight: 500, fontFamily: 'inherit' }}>
                <IconSend size={14} /> {t.sendToLatifa}
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {visibleMessages.map((msg, i) => {
            if (msg.from === 'system') {
              return (
                <div key={i} style={{
                  margin: '12px auto', maxWidth: 400, padding: '10px 16px',
                  background: '#F5F3FF', border: '1px dashed #7B4FAE', borderRadius: 10,
                  fontSize: 12, color: '#5B2D8E', textAlign: 'center', whiteSpace: 'pre-line',
                  fontFamily: "'IBM Plex Mono', monospace", animation: 'slideUp 0.3s ease'
                }}>
                  {lang === 'ar' ? msg.ar : msg.en}
                </div>
              );
            }
            const isCustomer = msg.from === 'customer';
            return (
              <div key={i} style={{
                display: 'flex', justifyContent: isCustomer ? (isRtl ? 'flex-start' : 'flex-end') : (isRtl ? 'flex-end' : 'flex-start'),
                marginBottom: 10, animation: 'slideUp 0.3s ease'
              }}>
                <div style={{
                  maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                  background: isCustomer ? '#DCF8C6' : '#fff',
                  border: isCustomer ? 'none' : '1px solid #E5E7EB',
                  fontSize: 14, lineHeight: 1.6, color: '#1A1A2E',
                  borderTopRightRadius: isCustomer && !isRtl ? 4 : 12,
                  borderTopLeftRadius: isCustomer && isRtl ? 4 : (!isCustomer && !isRtl ? 4 : 12),
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                }}>
                  {!isCustomer && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: agent?.color, marginBottom: 4 }}>
                      {lang === 'ar' ? agent?.nameAr : agent?.name}
                    </div>
                  )}
                  {lang === 'ar' ? msg.ar : msg.en}
                </div>
              </div>
            );
          })}
          {isPlaying && (
            <div style={{ display: 'flex', justifyContent: isRtl ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
              <div style={{ padding: '10px 18px', borderRadius: 12, background: '#fff', border: '1px solid #E5E7EB', display: 'flex', gap: 4 }}>
                {[0,1,2].map(d => (
                  <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9CA3AF', animation: `pulse 1.4s ${d * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// INBOX MODE — full 5-panel inbox
// ════════════════════════════════════════════════════════════════════
function InboxMode({ lang, t, industry, notify, setPage }) {
  const isRtl = lang === 'ar';
  const inboxConvos = industry.inboxConversations || [];
  const _initLabels = industry.inboxLabels || [];
  const _initViews = industry.inboxViews || [];

  // State
  const [sidebarFilter, setSidebarFilter] = useState({ type: 'all' });
  const [selectedConvoId, setSelectedConvoId] = useState(inboxConvos[0]?.id || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailOpen, setDetailOpen] = useState(true);
  const [composerMode, setComposerMode] = useState('reply');
  const [composerText, setComposerText] = useState('');
  const [localConvos, setLocalConvos] = useState(inboxConvos);
  const [localLabels, setLocalLabels] = useState(_initLabels);
  const [localViews, setLocalViews] = useState(_initViews);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [createLabelOpen, setCreateLabelOpen] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelNameAr, setNewLabelNameAr] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#3B82F6');
  const [createViewOpen, setCreateViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewNameAr, setNewViewNameAr] = useState('');
  const [addLabelOpen, setAddLabelOpen] = useState(false);
  const [slashHintOpen, setSlashHintOpen] = useState(false);
  const chatEndRef = useRef(null);
  const LABEL_COLORS = ['#EF4444', '#F59E0B', '#8B5CF6', '#10B981', '#3B82F6', '#F97316', '#EC4899', '#6366F1'];

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selectedConvoId]);

  // Filtering
  const filteredConvos = localConvos.filter(c => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(c.customerEn?.toLowerCase().includes(q) || c.customer?.includes(q))) return false;
    }
    const f = sidebarFilter;
    if (f.type === 'myInbox') return c.agent === 'abdullah';
    if (f.type === 'unassigned') return !c.agent;
    if (f.type === 'spam') return false;
    if (f.type === 'agent') return c.agent === f.value;
    if (f.type === 'channel') return c.channel === f.value;
    if (f.type === 'label') return c.labels?.includes(f.value);
    if (f.type === 'view') {
      const view = localViews.find(v => v.id === f.value);
      if (!view) return true;
      const fl = view.filter;
      if (fl.channel && c.channel !== fl.channel) return false;
      if (fl.status && c.status !== fl.status) return false;
      if (fl.labels) return fl.labels.some(l => c.labels?.includes(l));
      return true;
    }
    return true; // 'all'
  }).sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));

  const selectedConvo = localConvos.find(c => c.id === selectedConvoId) || filteredConvos[0];
  const selectedAgent = selectedConvo?.agent ? AGENTS[selectedConvo.agent] : null;

  // Counts
  const myInboxCount = localConvos.filter(c => c.agent === 'abdullah' && c.unread).length;
  const unassignedCount = localConvos.filter(c => !c.agent).length;

  const handleSend = () => {
    if (!composerText.trim() || !selectedConvo) return;
    const newMsg = {
      id: `msg-new-${Date.now()}`,
      from: composerMode === 'note' ? 'note' : 'customer',
      ...(composerMode === 'note' ? { author: 'You', authorAr: 'أنت' } : {}),
      ar: composerText,
      en: composerText,
      timestamp: new Date().toISOString(),
    };
    setLocalConvos(prev => prev.map(c =>
      c.id === selectedConvo.id ? { ...c, messages: [...c.messages, newMsg], lastMessageAt: new Date().toISOString() } : c
    ));
    setComposerText('');
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleReassign = (agentKey) => {
    setLocalConvos(prev => prev.map(c =>
      c.id === selectedConvo.id ? { ...c, agent: agentKey } : c
    ));
    setReassignOpen(false);
    if (notify) notify(lang === 'ar' ? `تم إسناد المحادثة إلى ${AGENTS[agentKey]?.nameAr}` : `Conversation reassigned to ${AGENTS[agentKey]?.name}`);
  };

  const handleStatusChange = (status) => {
    setLocalConvos(prev => prev.map(c =>
      c.id === selectedConvo.id ? { ...c, status } : c
    ));
    setStatusDropdownOpen(false);
  };

  const handleCreateLabel = () => {
    if (!newLabelName.trim()) return;
    const id = newLabelName.toLowerCase().replace(/\s+/g, '-');
    setLocalLabels(prev => [...prev, { id, name: newLabelName, nameAr: newLabelNameAr || newLabelName, color: newLabelColor }]);
    setNewLabelName(''); setNewLabelNameAr(''); setNewLabelColor('#3B82F6'); setCreateLabelOpen(false);
    if (notify) notify(lang === 'ar' ? `تم إنشاء التصنيف "${newLabelNameAr || newLabelName}"` : `Label "${newLabelName}" created`);
  };

  const handleCreateView = () => {
    if (!newViewName.trim()) return;
    const id = newViewName.toLowerCase().replace(/\s+/g, '-');
    setLocalViews(prev => [...prev, { id, name: newViewName, nameAr: newViewNameAr || newViewName, filter: { ...sidebarFilter } }]);
    setNewViewName(''); setNewViewNameAr(''); setCreateViewOpen(false);
    if (notify) notify(lang === 'ar' ? `تم حفظ العرض "${newViewNameAr || newViewName}"` : `View "${newViewName}" saved`);
  };

  const handleToggleLabel = (labelId) => {
    if (!selectedConvo) return;
    setLocalConvos(prev => prev.map(c => {
      if (c.id !== selectedConvo.id) return c;
      const has = (c.labels || []).includes(labelId);
      return { ...c, labels: has ? c.labels.filter(l => l !== labelId) : [...(c.labels || []), labelId] };
    }));
  };

  // ─── Sidebar Item ───
  const SidebarItem = ({ icon: Icon, label, count, active, onClick }) => (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
      borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13,
      background: active ? '#F3F0FF' : 'transparent', color: active ? '#5B2D8E' : '#6B7280',
      borderLeft: !isRtl && active ? '3px solid #5B2D8E' : '3px solid transparent',
      borderRight: isRtl && active ? '3px solid #5B2D8E' : '3px solid transparent',
      transition: 'all 0.15s', fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left',
    }}>
      <Icon size={16} stroke={1.5} />
      <span style={{ flex: 1 }}>{label}</span>
      {count > 0 && <span style={{
        background: '#5B2D8E', color: '#fff', fontSize: 10, fontWeight: 700,
        padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center'
      }}>{count}</span>}
    </button>
  );

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', animation: 'fadeIn 0.3s ease', overflow: 'hidden', margin: '-24px', border: '1px solid #E5E7EB' }}>

      {/* ═══ PANEL 2: Inbox Sidebar ═══ */}
      <div style={{
        width: 200, background: '#F9FAFB', flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderRight: isRtl ? 'none' : '1px solid #E5E7EB',
        borderLeft: isRtl ? '1px solid #E5E7EB' : 'none',
        overflow: 'auto',
      }}>
        {/* Inbox section */}
        <div style={{ padding: '12px 8px 4px' }}>
          <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, padding: '0 12px 6px', marginBottom: 2 }}>
            {t.conversations || 'Conversations'}
          </div>
          <SidebarItem icon={IconInbox} label={t.myInbox} count={myInboxCount} active={sidebarFilter.type === 'myInbox'} onClick={() => setSidebarFilter({ type: 'myInbox' })} />
          <SidebarItem icon={IconUsers} label={t.unassigned} count={unassignedCount} active={sidebarFilter.type === 'unassigned'} onClick={() => setSidebarFilter({ type: 'unassigned' })} />
          <SidebarItem icon={IconMailbox} label={t.allConversations} active={sidebarFilter.type === 'all'} onClick={() => setSidebarFilter({ type: 'all' })} />
          <SidebarItem icon={IconAlertCircle} label={t.spam} active={sidebarFilter.type === 'spam'} onClick={() => setSidebarFilter({ type: 'spam' })} />
        </div>

        <div style={{ height: 1, background: '#E5E7EB', margin: '8px 12px' }} />

        {/* Agents */}
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, padding: '0 12px 6px' }}>
            {t.agents}
          </div>
          {Object.entries(AGENTS).map(([key, ag]) => {
            const AIcon = AGENT_ICONS[key];
            return (
              <button key={key} onClick={() => setSidebarFilter({ type: 'agent', value: key })} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                background: sidebarFilter.type === 'agent' && sidebarFilter.value === key ? '#F3F0FF' : 'transparent',
                color: sidebarFilter.type === 'agent' && sidebarFilter.value === key ? '#5B2D8E' : '#6B7280',
                fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left', transition: 'all 0.15s',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                <AIcon size={14} stroke={1.5} color={ag.color} />
                <span style={{ flex: 1 }}>{lang === 'ar' ? ag.nameAr : ag.name}</span>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{localConvos.filter(cv => cv.agent === key).length}</span>
              </button>
            );
          })}
        </div>

        <div style={{ height: 1, background: '#E5E7EB', margin: '8px 12px' }} />

        {/* Channels */}
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, padding: '0 12px 6px' }}>
            {t.channels}
          </div>
          {['whatsapp', 'email', 'sms', 'web'].map(ch => {
            const ChIcon = CHANNEL_ICONS[ch];
            const labelKey = ch === 'web' ? 'webChat' : ch === 'email' ? 'emailChannel' : ch === 'sms' ? 'smsChannel' : 'whatsappChannel';
            return (
              <button key={ch} onClick={() => setSidebarFilter({ type: 'channel', value: ch })} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                background: sidebarFilter.type === 'channel' && sidebarFilter.value === ch ? '#F3F0FF' : 'transparent',
                color: sidebarFilter.type === 'channel' && sidebarFilter.value === ch ? '#5B2D8E' : '#6B7280',
                fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left', transition: 'all 0.15s',
              }}>
                <ChIcon size={14} stroke={1.5} color={CHANNEL_COLORS[ch]} />
                <span style={{ flex: 1 }}>{t[labelKey] || ch}</span>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{localConvos.filter(cv => cv.channel === ch).length}</span>
              </button>
            );
          })}
        </div>

        <div style={{ height: 1, background: '#E5E7EB', margin: '8px 12px' }} />

        {/* Labels */}
        <div style={{ padding: '4px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px 6px' }}>
            <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{t.labels}</span>
            <button onClick={() => setCreateLabelOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B2D8E', fontSize: 14, fontWeight: 700, padding: 0, lineHeight: 1 }}>+</button>
          </div>
          {localLabels.map(label => (
            <button key={label.id} onClick={() => setSidebarFilter({ type: 'label', value: label.id })} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
              borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
              background: sidebarFilter.type === 'label' && sidebarFilter.value === label.id ? '#F3F0FF' : 'transparent',
              color: sidebarFilter.type === 'label' && sidebarFilter.value === label.id ? '#5B2D8E' : '#6B7280',
              fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left', transition: 'all 0.15s',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: label.color, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{lang === 'ar' ? label.nameAr : label.name}</span>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{localConvos.filter(cv => cv.labels?.includes(label.id)).length}</span>
            </button>
          ))}
        </div>

        <div style={{ height: 1, background: '#E5E7EB', margin: '8px 12px' }} />

        {/* Custom Views */}
        <div style={{ padding: '4px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px 6px' }}>
            <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{t.customViews}</span>
            <button onClick={() => setCreateViewOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B2D8E', fontSize: 14, fontWeight: 700, padding: 0, lineHeight: 1 }}>+</button>
          </div>
          {localViews.map(view => (
            <button key={view.id} onClick={() => setSidebarFilter({ type: 'view', value: view.id })} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
              borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
              background: sidebarFilter.type === 'view' && sidebarFilter.value === view.id ? '#F3F0FF' : 'transparent',
              color: sidebarFilter.type === 'view' && sidebarFilter.value === view.id ? '#5B2D8E' : '#6B7280',
              fontFamily: 'inherit', textAlign: isRtl ? 'right' : 'left', transition: 'all 0.15s',
            }}>
              <IconEye size={14} stroke={1.5} />
              <span>{lang === 'ar' ? view.nameAr : view.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ═══ PANEL 3: Conversation List ═══ */}
      <div style={{
        width: 280, background: '#fff', flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderRight: isRtl ? 'none' : '1px solid #E5E7EB',
        borderLeft: isRtl ? '1px solid #E5E7EB' : 'none',
      }}>
        {/* Search */}
        <div style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ position: 'relative' }}>
            <IconSearch size={14} stroke={1.5} style={{ position: 'absolute', top: 9, [isRtl ? 'right' : 'left']: 10, color: '#6B7280' }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t.search} style={{
              width: '100%', padding: '8px 12px', [isRtl ? 'paddingRight' : 'paddingLeft']: 30,
              borderRadius: 8, border: '1px solid #E5E7EB', background: '#F3F4F6', color: '#1A1A2E',
              fontSize: 13, outline: 'none', fontFamily: 'inherit',
            }} />
          </div>
        </div>

        {/* Conversation rows */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {filteredConvos.map(c => {
            const isActive = c.id === (selectedConvo?.id);
            const ag = c.agent ? AGENTS[c.agent] : null;
            const lastMsg = c.messages?.filter(m => m.from !== 'system').slice(-1)[0];
            const ChIcon = CHANNEL_ICONS[c.channel] || IconMessage;
            const convLabels = (c.labels || []).map(lid => localLabels.find(l => l.id === lid)).filter(Boolean);
            return (
              <div key={c.id} onClick={() => setSelectedConvoId(c.id)} style={{
                padding: '10px 12px', cursor: 'pointer', transition: 'background 0.15s',
                background: isActive ? '#F3F0FF' : '#fff',
                borderLeft: !isRtl && isActive ? '3px solid #5B2D8E' : '3px solid transparent',
                borderRight: isRtl && isActive ? '3px solid #5B2D8E' : '3px solid transparent',
                borderBottom: '1px solid #E5E7EB',
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {/* Avatar */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', background: avatarColor(c.id),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff',
                    }}>
                      {getInitials(c.customerEn, c.customer, lang)}
                    </div>
                    {c.unread && <div style={{
                      position: 'absolute', top: -2, [isRtl ? 'left' : 'right']: -2,
                      width: 8, height: 8, borderRadius: '50%', background: '#3B82F6', border: '2px solid #fff'
                    }} />}
                    {ag && (
                      <div style={{
                        position: 'absolute', bottom: -2, [isRtl ? 'left' : 'right']: -4,
                        width: 18, height: 18, borderRadius: '50%', background: ag.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #fff',
                      }}>
                        {React.createElement(AGENT_ICONS[c.agent], { size: 10, color: ag.color, stroke: 2 })}
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontWeight: c.unread ? 700 : 500, fontSize: 13, color: '#1A1A2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lang === 'ar' ? c.customer : c.customerEn}
                      </span>
                      <span style={{ fontSize: 10, color: '#6B7280', flexShrink: 0, marginLeft: 4, marginRight: 4 }}>
                        {relativeTime(c.lastMessageAt, lang)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                      {lastMsg ? (lang === 'ar' ? lastMsg.ar : lastMsg.en) : ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: CHANNEL_COLORS[c.channel] }}>
                        <ChIcon size={11} stroke={1.5} />
                        {t[c.channel === 'web' ? 'webChat' : c.channel === 'email' ? 'emailChannel' : c.channel === 'sms' ? 'smsChannel' : 'whatsappChannel'] || c.channel}
                      </span>
                      {convLabels.slice(0, 2).map(l => (
                        <span key={l.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 2, padding: '0px 5px', borderRadius: 4,
                          background: l.color + '18', color: l.color, fontSize: 10, fontWeight: 600,
                        }}>
                          <div style={{ width: 5, height: 5, borderRadius: '50%', background: l.color }} />
                          {lang === 'ar' ? l.nameAr : l.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredConvos.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>
              {t.noConversations || 'No conversations'}
            </div>
          )}
        </div>
      </div>

      {/* ═══ PANEL 4: Chat Area ═══ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F3F4F6', minWidth: 0 }}>
        {selectedConvo ? (
          <>
            {/* Chat header */}
            <div style={{
              padding: '10px 16px', background: '#fff', borderBottom: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: avatarColor(selectedConvo.id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff',
                }}>
                  {getInitials(selectedConvo.customerEn, selectedConvo.customer, lang)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>
                    {lang === 'ar' ? selectedConvo.customer : selectedConvo.customerEn}
                    <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 400, marginLeft: 8, marginRight: 8 }}>{selectedConvo.phone}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    {/* Channel badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4,
                      background: CHANNEL_COLORS[selectedConvo.channel] + '22', color: CHANNEL_COLORS[selectedConvo.channel], fontSize: 11, fontWeight: 600,
                    }}>
                      {React.createElement(CHANNEL_ICONS[selectedConvo.channel] || IconMessage, { size: 11, stroke: 2 })}
                      {t[selectedConvo.channel === 'web' ? 'webChat' : selectedConvo.channel === 'email' ? 'emailChannel' : selectedConvo.channel === 'sms' ? 'smsChannel' : 'whatsappChannel'] || selectedConvo.channel}
                    </span>
                    {/* Status badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4,
                      background: STATUS_COLORS[selectedConvo.status] + '22', color: STATUS_COLORS[selectedConvo.status], fontSize: 11, fontWeight: 600,
                    }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: STATUS_COLORS[selectedConvo.status] }} />
                      {t['status' + selectedConvo.status.charAt(0).toUpperCase() + selectedConvo.status.slice(1)] || selectedConvo.status}
                    </span>
                    {/* Assigned agent */}
                    <div style={{ position: 'relative' }}>
                      <button onClick={() => { setReassignOpen(!reassignOpen); setStatusDropdownOpen(false); }} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4,
                        background: selectedAgent ? selectedAgent.bg + '22' : '#E5E7EB', border: 'none', cursor: 'pointer',
                        color: selectedAgent?.color || '#9CA3AF', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                      }}>
                        {selectedAgent ? (lang === 'ar' ? selectedAgent.nameAr : selectedAgent.name) : t.unassigned}
                        <IconChevronDown size={10} />
                      </button>
                      {reassignOpen && (
                        <div style={{
                          position: 'absolute', top: '100%', [isRtl ? 'right' : 'left']: 0, marginTop: 4,
                          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: 4,
                          zIndex: 100, minWidth: 140, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        }}>
                          {Object.entries(AGENTS).map(([key, ag]) => (
                            <button key={key} onClick={() => handleReassign(key)} style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                              borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12,
                              background: 'transparent', color: '#1A1A2E', fontFamily: 'inherit',
                              textAlign: isRtl ? 'right' : 'left',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              {React.createElement(AGENT_ICONS[key], { size: 12, color: ag.color, stroke: 2 })}
                              {lang === 'ar' ? ag.nameAr : ag.name}
                              {selectedConvo.agent === key && <IconCheck size={12} color="#22C55E" style={{ marginLeft: 'auto', marginRight: 'auto' }} />}
                            </button>
                          ))}
                          <button onClick={() => handleReassign(null)} style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                            borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12,
                            background: 'transparent', color: '#9CA3AF', fontFamily: 'inherit',
                            textAlign: isRtl ? 'right' : 'left',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {t.unassigned}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Toggle detail panel */}
              <button onClick={() => setDetailOpen(!detailOpen)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4,
              }}>
                {detailOpen
                  ? (isRtl ? <IconChevronLeft size={18} /> : <IconChevronRight size={18} />)
                  : (isRtl ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />)
                }
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }} onClick={() => { setReassignOpen(false); setStatusDropdownOpen(false); }}>
              {(() => {
                let lastDate = '';
                return selectedConvo.messages.map((msg, i) => {
                  const msgDate = dateLabel(msg.timestamp, lang);
                  const showDate = msgDate !== lastDate;
                  lastDate = msgDate;

                  return (
                    <React.Fragment key={msg.id || i}>
                      {showDate && msgDate && (
                        <div style={{ textAlign: 'center', margin: '16px 0 8px', fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                          <span style={{ background: '#fff', padding: '2px 12px', borderRadius: 10 }}>{msgDate}</span>
                        </div>
                      )}
                      {/* System message */}
                      {msg.from === 'system' && (
                        <div style={{
                          margin: '8px auto', maxWidth: 420, padding: '8px 14px',
                          background: '#F5F3FF', border: '1px dashed #5B2D8E', borderRadius: 10,
                          fontSize: 12, color: '#5B2D8E', textAlign: 'center', whiteSpace: 'pre-line',
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}>
                          {lang === 'ar' ? msg.ar : msg.en}
                        </div>
                      )}
                      {/* AI message */}
                      {msg.from === 'ai' && (
                        <div style={{ display: 'flex', justifyContent: isRtl ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                          <div style={{
                            maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                            background: '#F5F3FF', border: '1px solid #DDD6FE',
                            fontSize: 14, lineHeight: 1.6, color: '#4C1D95',
                            borderTopLeftRadius: !isRtl ? 4 : 12, borderTopRightRadius: isRtl ? 4 : 12,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                              <IconRobot size={12} color="#8B5CF6" stroke={2} />
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#8B5CF6' }}>{t.aiAutoReply}</span>
                            </div>
                            {lang === 'ar' ? msg.ar : msg.en}
                            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4, textAlign: isRtl ? 'left' : 'right' }}>
                              {relativeTime(msg.timestamp, lang)}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Internal note */}
                      {msg.from === 'note' && (
                        <div style={{ display: 'flex', justifyContent: isRtl ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                          <div style={{
                            maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                            background: '#FFFBEB', border: '1px solid #F59E0B',
                            fontSize: 14, lineHeight: 1.6, color: '#D97706',
                            borderTopLeftRadius: !isRtl ? 4 : 12, borderTopRightRadius: isRtl ? 4 : 12,
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#F59E0B', marginBottom: 4 }}>
                              {lang === 'ar' ? msg.authorAr : msg.author} — {t.internalNote}
                            </div>
                            {lang === 'ar' ? msg.ar : msg.en}
                            <div style={{ fontSize: 9, color: '#D97706', marginTop: 4 }}>{t.noteHelperText}</div>
                            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2, textAlign: isRtl ? 'left' : 'right' }}>
                              {relativeTime(msg.timestamp, lang)}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Customer message */}
                      {msg.from === 'customer' && (
                        <div style={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
                          <div style={{
                            maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                            background: '#DCF8C6', fontSize: 14, lineHeight: 1.6, color: '#1A1A2E',
                            borderTopRightRadius: !isRtl ? 4 : 12, borderTopLeftRadius: isRtl ? 4 : 12,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          }}>
                            {lang === 'ar' ? msg.ar : msg.en}
                            <div style={{ fontSize: 10, color: '#4B5563', marginTop: 4, textAlign: isRtl ? 'left' : 'right' }}>
                              {relativeTime(msg.timestamp, lang)}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Agent message */}
                      {msg.from === 'agent' && (
                        <div style={{ display: 'flex', justifyContent: isRtl ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                          <div style={{
                            maxWidth: '65%', padding: '10px 14px', borderRadius: 12,
                            background: '#fff', border: '1px solid #E5E7EB',
                            fontSize: 14, lineHeight: 1.6, color: '#1A1A2E',
                            borderTopLeftRadius: !isRtl ? 4 : 12, borderTopRightRadius: isRtl ? 4 : 12,
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: AGENTS[msg.agent]?.color || '#9CA3AF', marginBottom: 4 }}>
                              {lang === 'ar' ? AGENTS[msg.agent]?.nameAr : AGENTS[msg.agent]?.name}
                            </div>
                            {lang === 'ar' ? msg.ar : msg.en}
                            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4, textAlign: isRtl ? 'left' : 'right' }}>
                              {relativeTime(msg.timestamp, lang)}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                });
              })()}
              <div ref={chatEndRef} />
            </div>

            {/* Composer */}
            <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #E5E7EB', flexShrink: 0 }}>
              {/* Reply/Note toggle */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                <button onClick={() => setComposerMode('reply')} style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: composerMode === 'reply' ? '#10B981' : '#F3F4F6',
                  color: composerMode === 'reply' ? '#fff' : '#6B7280', fontFamily: 'inherit',
                }}>
                  {t.reply}
                </button>
                <button onClick={() => setComposerMode('note')} style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: composerMode === 'note' ? '#F59E0B' : '#F3F4F6',
                  color: composerMode === 'note' ? '#fff' : '#6B7280', fontFamily: 'inherit',
                }}>
                  {t.internalNote}
                </button>
              </div>
              {/* Toolbar */}
              <div style={{
                display: 'flex', gap: 2, padding: '4px 8px', borderRadius: '8px 8px 0 0',
                background: composerMode === 'note' ? '#FFFBEB' : '#F3F4F6',
                border: composerMode === 'note' ? '1px solid #F59E0B' : '1px solid #E5E7EB',
                borderBottom: 'none',
              }}>
                {[IconBold, IconItalic, IconMoodSmile, IconPaperclip].map((Icon, i) => (
                  <button key={i} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                    color: composerMode === 'note' ? '#F59E0B' : '#6B7280',
                  }}>
                    <Icon size={14} stroke={1.5} />
                  </button>
                ))}
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setSlashHintOpen(!slashHintOpen)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                    color: composerMode === 'note' ? '#F59E0B' : '#6B7280',
                  }}>
                    <IconSlash size={14} stroke={1.5} />
                  </button>
                  {slashHintOpen && (
                    <div style={{
                      position: 'absolute', bottom: '100%', [isRtl ? 'right' : 'left']: 0, marginBottom: 6,
                      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 180, zIndex: 50,
                    }}>
                      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, marginBottom: 4 }}>
                        {lang === 'ar' ? 'اختصارات سريعة' : 'Quick shortcuts'}
                      </div>
                      {[
                        { cmd: '/assign', desc: lang === 'ar' ? 'إسناد المحادثة' : 'Assign conversation' },
                        { cmd: '/resolve', desc: lang === 'ar' ? 'حل المحادثة' : 'Resolve conversation' },
                        { cmd: '/escalate', desc: lang === 'ar' ? 'تصعيد المحادثة' : 'Escalate conversation' },
                        { cmd: '/note', desc: lang === 'ar' ? 'إضافة ملاحظة' : 'Add internal note' },
                        { cmd: '/ticket', desc: lang === 'ar' ? 'إنشاء تذكرة' : 'Create ticket' },
                      ].map(s => (
                        <div key={s.cmd} style={{ display: 'flex', gap: 8, padding: '3px 4px', fontSize: 12 }}>
                          <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#5B2D8E', fontWeight: 600, minWidth: 70 }}>{s.cmd}</span>
                          <span style={{ color: '#6B7280' }}>{s.desc}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Textarea + Send */}
              <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 8,
                background: composerMode === 'note' ? '#FFFBEB' : '#F3F4F6',
                border: composerMode === 'note' ? '1px solid #F59E0B' : '1px solid #E5E7EB',
                borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '8px 10px',
              }}>
                <textarea
                  value={composerText}
                  onChange={e => setComposerText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={composerMode === 'note' ? (t.noteHelperText) : t.typeMessage}
                  rows={2}
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none',
                    color: composerMode === 'note' ? '#F59E0B' : '#1A1A2E', fontSize: 13, fontFamily: 'inherit',
                    lineHeight: 1.5,
                  }}
                />
                <button onClick={handleSend} style={{
                  background: '#10B981', border: 'none', borderRadius: 8, padding: '8px 12px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: composerText.trim() ? 1 : 0.4,
                }}>
                  <IconSend size={16} color="#fff" stroke={2} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>
            {t.noConversations || 'Select a conversation'}
          </div>
        )}
      </div>

      {/* ═══ PANEL 5: Detail Panel ═══ */}
      {detailOpen && selectedConvo && (
        <div style={{
          width: 260, background: '#fff', flexShrink: 0, overflow: 'auto',
          borderLeft: isRtl ? 'none' : '1px solid #E5E7EB',
          borderRight: isRtl ? '1px solid #E5E7EB' : 'none',
          transition: 'width 0.3s ease',
        }}>
          {/* Customer card */}
          <div style={{ padding: '20px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: avatarColor(selectedConvo.id),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 700, color: '#fff', margin: '0 auto 10px',
            }}>
              {getInitials(selectedConvo.customerEn, selectedConvo.customer, lang)}
            </div>
            <div onClick={() => { if (setPage) setPage('crm'); }} style={{
              fontWeight: 600, fontSize: 15, color: '#1A1A2E', cursor: setPage ? 'pointer' : 'default',
            }}
            onMouseEnter={e => { if (setPage) e.currentTarget.style.color = '#B794D4'; }}
            onMouseLeave={e => { if (setPage) e.currentTarget.style.color = '#fff'; }}>
              {lang === 'ar' ? selectedConvo.customer : selectedConvo.customerEn}
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{selectedConvo.phone}</div>
            {selectedConvo.email && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{selectedConvo.email}</div>}
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
              {t.customerSinceLabel} {new Date(selectedConvo.customerSince).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'short' })}
            </div>
          </div>

          {/* Assignment */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{t.assignedTo}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#1A1A2E' }}>
              {selectedAgent ? (
                <>
                  {React.createElement(AGENT_ICONS[selectedConvo.agent], { size: 14, color: selectedAgent.color, stroke: 2 })}
                  {lang === 'ar' ? selectedAgent.nameAr : selectedAgent.name} — {lang === 'ar' ? selectedAgent.roleAr : selectedAgent.role}
                </>
              ) : (
                <span style={{ color: '#6B7280' }}>{t.unassigned}</span>
              )}
            </div>
          </div>

          {/* Status */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{t.changeStatus}</div>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setStatusDropdownOpen(!statusDropdownOpen); setReassignOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6,
                border: '1px solid #E5E7EB', background: STATUS_COLORS[selectedConvo.status] + '22',
                color: STATUS_COLORS[selectedConvo.status], fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[selectedConvo.status] }} />
                {t['status' + selectedConvo.status.charAt(0).toUpperCase() + selectedConvo.status.slice(1)] || selectedConvo.status}
                <IconChevronDown size={12} />
              </button>
              {statusDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', [isRtl ? 'right' : 'left']: 0, marginTop: 4,
                  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: 4,
                  zIndex: 100, minWidth: 130, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}>
                  {['active', 'resolved', 'escalated'].map(s => (
                    <button key={s} onClick={() => handleStatusChange(s)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                      borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12,
                      background: 'transparent', color: STATUS_COLORS[s], fontFamily: 'inherit',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[s] }} />
                      {t['status' + s.charAt(0).toUpperCase() + s.slice(1)] || s}
                      {selectedConvo.status === s && <IconCheck size={12} color="#22C55E" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Labels */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{t.labels}</span>
              <button onClick={() => setAddLabelOpen(!addLabelOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B2D8E', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>+ ADD</button>
            </div>
            {addLabelOpen && (
              <div style={{ marginBottom: 8, padding: 8, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                {localLabels.map(label => {
                  const isOn = (selectedConvo.labels || []).includes(label.id);
                  return (
                    <button key={label.id} onClick={() => handleToggleLabel(label.id)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
                      borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12,
                      background: 'transparent', fontFamily: 'inherit', color: '#1A1A2E',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: label.color }} />
                      <span style={{ flex: 1 }}>{lang === 'ar' ? label.nameAr : label.name}</span>
                      {isOn && <IconCheck size={12} color="#10B981" />}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(selectedConvo.labels || []).map(lid => {
                const label = localLabels.find(l => l.id === lid);
                if (!label) return null;
                return (
                  <span key={lid} onClick={() => handleToggleLabel(lid)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10,
                    background: label.color + '22', color: label.color, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: label.color }} />
                    {lang === 'ar' ? label.nameAr : label.name}
                    <IconX size={10} stroke={2} />
                  </span>
                );
              })}
              {(!selectedConvo.labels || selectedConvo.labels.length === 0) && (
                <span style={{ fontSize: 12, color: '#6B7280' }}>—</span>
              )}
            </div>
          </div>

          {/* Tags */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Tags</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(lang === 'ar' ? selectedConvo.tagsAr : selectedConvo.tags || []).map((tag, i) => (
                <span key={i} style={{
                  padding: '2px 8px', borderRadius: 10, background: '#E5E7EB', color: '#9CA3AF', fontSize: 11, fontWeight: 500,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Groups */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              {t.groups || 'Groups'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(lang === 'ar' ? selectedConvo.groupsAr : selectedConvo.groups || []).map((g, i) => (
                <span key={i} style={{
                  padding: '2px 8px', borderRadius: 10, background: '#5B2D8E22', color: '#5B2D8E', fontSize: 11, fontWeight: 500,
                }}>
                  {g}
                </span>
              ))}
              {(!selectedConvo.groups || selectedConvo.groups.length === 0) && (
                <span style={{ fontSize: 12, color: '#6B7280' }}>—</span>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ padding: '12px 16px' }}>
            <button onClick={() => { if (setPage) setPage('crm'); }} style={{
              width: '100%', padding: '8px 0', borderRadius: 6, border: '1px solid #5B2D8E',
              background: 'transparent', color: '#5B2D8E', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6,
            }}>
              {t.viewInCrm}
            </button>
            <button style={{
              width: '100%', padding: '8px 0', borderRadius: 6, border: '1px solid #E5E7EB',
              background: 'transparent', color: '#9CA3AF', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <IconTicket size={14} stroke={1.5} style={{ verticalAlign: 'middle', marginRight: 4, marginLeft: 4 }} />
              {t.createTicket}
            </button>
          </div>
        </div>
      )}

      {/* ═══ Create Label Modal ═══ */}
      {createLabelOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={() => setCreateLabelOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 340, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#1A1A2E', marginBottom: 16 }}>{t.createLabel}</div>
            <input value={newLabelName} onChange={e => setNewLabelName(e.target.value)} placeholder={lang === 'ar' ? 'اسم التصنيف (EN)' : 'Label name'}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
            <input value={newLabelNameAr} onChange={e => setNewLabelNameAr(e.target.value)} placeholder={lang === 'ar' ? 'اسم التصنيف (AR)' : 'Arabic name (optional)'}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', fontFamily: 'inherit', marginBottom: 12 }} />
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{lang === 'ar' ? 'اللون' : 'Color'}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {LABEL_COLORS.map(c => (
                <button key={c} onClick={() => setNewLabelColor(c)} style={{
                  width: 28, height: 28, borderRadius: '50%', background: c, border: newLabelColor === c ? '3px solid #1A1A2E' : '3px solid transparent',
                  cursor: 'pointer', transition: 'border 0.15s',
                }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreateLabelOpen(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#6B7280' }}>
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button onClick={handleCreateLabel} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#5B2D8E', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', opacity: newLabelName.trim() ? 1 : 0.4 }}>
                {t.createLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Create View Modal ═══ */}
      {createViewOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={() => setCreateViewOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 340, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#1A1A2E', marginBottom: 8 }}>{t.createView}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
              {lang === 'ar' ? 'سيتم حفظ الفلتر الحالي كعرض مخصص' : 'Current filter will be saved as a custom view'}
            </div>
            <input value={newViewName} onChange={e => setNewViewName(e.target.value)} placeholder={lang === 'ar' ? 'اسم العرض (EN)' : 'View name'}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
            <input value={newViewNameAr} onChange={e => setNewViewNameAr(e.target.value)} placeholder={lang === 'ar' ? 'اسم العرض (AR)' : 'Arabic name (optional)'}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', fontFamily: 'inherit', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreateViewOpen(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#6B7280' }}>
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button onClick={handleCreateView} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#5B2D8E', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', opacity: newViewName.trim() ? 1 : 0.4 }}>
                {t.createView}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ════════════════════════════════════════════════════════════════════
export default function Conversations({ lang, t, industry, demoPhase, setDemoPhase, notify, setPage }) {
  const isDemoActive = demoPhase >= 1 && demoPhase <= 3;

  if (isDemoActive) {
    return <DemoMode lang={lang} t={t} industry={industry} demoPhase={demoPhase} setDemoPhase={setDemoPhase} />;
  }
  return <InboxMode lang={lang} t={t} industry={industry} notify={notify} setPage={setPage} />;
}
